import { useState, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface Finding { line: number; severity: string; message: string; suggestion: string }

export function CodeReviewPanel({ onClose: _onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState(false)
  const [findings, setFindings] = useState<Finding[]>([])
  const [reviewed, setReviewed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runReview = useCallback(async () => {
    const { openTabs, activeTabId } = useAppStore.getState()
    const activeTab = openTabs.find(t => t.id === activeTabId)
    if (!activeTab) { setError('No file open. Open a file first.'); return }
    if (!activeTab.content.trim()) { setError('File is empty.'); return }

    setLoading(true); setFindings([]); setError(null); setReviewed(false)
    try {
      const res = await authFetch(apiUrl('/api/ai-tools/review'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: activeTab.content, language: activeTab.language }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        setError(`Server error ${res.status}: ${text.slice(0, 200)}`)
        return
      }
      const data = await res.json()
      setFindings(data.findings ?? [])
      setReviewed(true)
    } catch (e: any) {
      setError(`Request failed: ${e?.message ?? 'Unknown error'}. Is the server running?`)
    } finally { setLoading(false) }
  }, [])

  const severityColor: Record<string, string> = {
    error: 'var(--red)', warning: 'var(--accent-3)', info: 'var(--accent)',
  }

  const activeTab = useAppStore(s => s.openTabs.find(t => t.id === s.activeTabId))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>🔍</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>AI Code Review</span>
        <div style={{ flex: 1 }} />
        <button onClick={runReview} disabled={loading} style={{
          padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(124,110,245,0.3)',
          background: 'rgba(124,110,245,0.1)', color: 'var(--accent)',
          cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
          opacity: loading ? 0.5 : 1,
        }}>
          {loading ? 'Reviewing...' : '🔍 Review Current File'}
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {error && (
          <div style={{ padding: '10px 14px', background: 'rgba(240,112,112,0.08)', borderBottom: '1px solid rgba(240,112,112,0.2)' }}>
            <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>⚠ {error}</div>
          </div>
        )}
        {!reviewed && !loading && !error && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            {activeTab ? `Click "Review" to scan ${activeTab.name}` : 'Open a file to review'}
          </div>
        )}
        {loading && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            Analyzing code with AI... this may take a few seconds
          </div>
        )}
        {reviewed && findings.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
            <div style={{ color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700 }}>No issues found!</div>
          </div>
        )}
        {findings.map((f, i) => (
          <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{
                fontSize: 8, padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)', fontWeight: 700,
                background: `${severityColor[f.severity] ?? 'var(--text-2)'}20`,
                color: severityColor[f.severity] ?? 'var(--text-2)',
              }}>{f.severity.toUpperCase()}</span>
              <span style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>L{f.line}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-0)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>{f.message}</div>
            {f.suggestion && (
              <div style={{ fontSize: 10, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', marginTop: 4, padding: '4px 8px', background: 'rgba(95,212,164,0.06)', borderRadius: 4 }}>
                💡 {f.suggestion}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
